import {ITEMS_MENU_SELECTOR} from 'common/components/items-menu/types';

import {ENTER_KEY_CODE, SPACE_KEY_CODE} from './dom';

const focusableElements = [
  ITEMS_MENU_SELECTOR,
  `a[href]:not([tabindex^="-"])`,
  `area[href]`,
  `button:not([disabled])`,
  `embed`,
  `iframe`,
  `input:not([disabled])`,
  `mp-autoreisizing-input:not([disabled])`,
  `.mp-checkbox-wrapper:not([disabled])`,
  `mp-clickable-input:not([disabled])`,
  `[mp-email-recipients-input]:not([data-disabled])`,
  `mp-file-uploader:not([disabled])`,
  `mp-icon-input:not([disabled])`,
  `mp-select:not([disabled])`,
  `mp-tag-input:not([disabled])`,
  `[mp-revealable-text-editor]:not([disabled])`,
  `object`,
  `select:not([disabled])`,
  `textarea:not([disabled])`,
  `[contenteditable]`,
  `[tabindex]:not([tabindex^="-"])`,
].join(`,`);

export function handleKeyPress(ev: KeyboardEvent) {
  if (ev.keyCode === ENTER_KEY_CODE || ev.keyCode === SPACE_KEY_CODE) {
    ev.preventDefault();
    ev.currentTarget?.dispatchEvent(
      new MouseEvent(`click`, {
        view: window,
        bubbles: true,
        cancelable: true,
      }),
    );
  }
}

function isFocusableElement(el: HTMLElement) {
  // if the node is not visible due to a size of 0, we do not want to it to be eligibles
  return (
    el.matches(focusableElements) &&
    Boolean(el.offsetWidth || el.offsetHeight || el.getClientRects().length) &&
    el.getAttribute(`not-focusable`) === null &&
    el.getAttribute(`disabled`) === null
  );
}

/**
 * This returns a list of children that are focusable. This includes any item with a non-negative tab index,
 * anchors, buttons, and custom mp-* objects. See the exported focusableElements for a list of all possible values
 */
export function getFocusableChildren(
  node: null | HTMLElement | HTMLSlotElement | Element | ShadowRoot,
  descendants = new Set<HTMLElement>(),
) {
  // Note that @node can actually be null because lastElementChild can be null;
  // If the browser supports Constructible Stylesheets, a Panel component can be completely childless without even a <style> tag.
  node?.querySelectorAll<HTMLElement>(`*`).forEach((child) => {
    /**
     * for slotted elements, we want to maintain insert order since their place in DOM
     * is not representative of where they visually render, the invariant here is that
     * the element being iterated here will be visited when its `slot` parent is seen
     * in a deeper level within its containing ShadowDOM boundary
     */
    if (child.assignedSlot === null) {
      isFocusableElement(child) && descendants.add(child);
      child.shadowRoot && getFocusableChildren(child.shadowRoot.lastElementChild, descendants);
    }

    const assignedElements = (child as HTMLSlotElement).assignedElements?.call(child);
    if (assignedElements) {
      assignedElements.forEach((slottedNode) => {
        isFocusableElement(slottedNode as HTMLElement) && descendants.add(slottedNode as HTMLElement);
        slottedNode?.shadowRoot && getFocusableChildren(slottedNode?.shadowRoot.lastElementChild, descendants);
      });
    }
  });

  return Array.from(descendants);
}

/**
 * This gets the current active element on the page. It starts at the root document
 * and will pierce any ShadowDOM if it is encountered.
 */
export function getActiveElement(node: Document | ShadowRoot = document) {
  const activeElement = node.activeElement;
  if (activeElement && !activeElement?.shadowRoot) {
    return activeElement;
  } else if (node.activeElement?.shadowRoot && node.activeElement.shadowRoot.activeElement !== null) {
    return getActiveElement(node.activeElement.shadowRoot);
  }
  return activeElement;
}
